import { installScrollPolyfills } from '@app-iptv/ui/compat'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { wireRuntimeConfig } from './api'
import { App } from './App'
import { deferAfterFirstPaint } from './boot-defer'
import { bootstrap, setupMockServer } from './bootstrap'
import { initSentryIfConfigured } from './observability/sentry'
import { applyPackagedTvScale, applyTvUiScale } from './packaged-tv-scale'
import { startQoe } from './qoe'
import {
  API_SEED_ORIGINS,
  getActiveApiUrl,
  getActiveSiteUrl,
  loadRuntimeConfig,
  resetApiUrl,
  rotateApiUrl,
} from './runtime-config'
import { initSwAutoRecovery } from './sw-recovery'
import { registerServiceWorker } from './sw-register'
// `fonts.css` é importado AQUI, como módulo próprio, e NÃO via `@import` de
// dentro do index.css — de propósito. O Vite expande `@import` de CSS DENTRO do
// plugin `vite:css`, então um arquivo só-@import-ado nunca chega ao pipeline de
// transform como módulo: o plugin `tv-font-woff-only` (vite.config.ts), que
// precisa podar as sources woff2 antes do resolve dos `url()`, jamais o veria.
// Já aconteceu: o build de TV saiu com woff2 embutido, que NÃO CARREGA no
// Chromium 38 (piso) — e falha de @font-face é MUDA, a fonte só sumia na TV.
// Importado aqui, `src/fonts.css` é um módulo de verdade e o plugin o intercepta.
// Vem ANTES do index.css para que os @font-face sejam registrados antes das
// regras que os usam.
import './fonts.css'
import './index.css'

// P6 (perf/tv-hero-coldstart): marcas de boot pra medir o SPLIT do cold start no
// device (.ipk). São 4: `boot:poly-eval-end` (injetada no index.html logo após o
// chunk de polyfills-legacy — ver tvBootPerfPolyMarkPlugin em vite.config.ts),
// `boot:vendor-entry-eval-end` (aqui), `boot:create-root` e `boot:first-paint`.
// Só INSTRUMENTA — não corta polyfill nesta leva; a decisão da lente de polyfill
// (P7) fica pro Fernando com o número real na mão. `performance.mark` é Chr28+
// (piso Chr38 OK); tudo feature-detected + try/catch pra JAMAIS derrubar o boot
// no fleet 2017. Loga também `performance.now()` pra ler o split via console/CDP.
function markBoot(name: string): void {
  try {
    const p = typeof performance !== 'undefined' ? performance : undefined
    if (p && typeof p.mark === 'function') p.mark(name)
    const t = p && typeof p.now === 'function' ? Math.round(p.now()) : 0
    console.warn(`[boot-perf] ${name} t=${t}ms`)
  } catch {
    // instrumentação nunca pode quebrar o boot
  }
}

// Marca 2/4: vendor + entry avaliados. Este é o 1º código executável do módulo
// de entry — no bundle legacy (SystemJS) as dependências (chunk `vendor`, react,
// etc.) executam ANTES do corpo do entry, então aqui elas já rodaram. O intervalo
// desde `boot:poly-eval-end` isola o custo de parse/eval do vendor+entry.
markBoot('boot:vendor-entry-eval-end')

// Polyfills de scroll ANTES de qualquer boot que toque o DOM: Chr38/53 (piso do
// produto) não têm Element.prototype.scrollBy/scrollTo — sem isso a TV ao vivo e
// os rails explodem ("scrollBy is not a function") e caem no error boundary.
// Feature-detected: no-op no Chr moderno. O módulo mora em `packages/ui`
// (@app-iptv/ui/compat) porque o call site mais quente do compat de scroll é o
// <Focusable>, que vive lá — e packages/ui não pode importar de apps/tv.
installScrollPolyfills()

// Âncora de escala do canvas em TV EMPACOTADA (camada 2 — rede de segurança),
// ANTES de qualquer render. NO-OP fora de TV empacotada (web/mobile: retorna
// cedo, zero custo/risco). Quando é TV empacotada cujo firmware ignorou a meta
// viewport `width=1920` (camada 1, no build), reforça a meta por JS e, se ainda
// assim não pegar, aplica o fallback de escala no #root ANTES do React montar
// (evita flash de layout "espremido" antes do transform). Idempotente e à prova
// de erro — nunca derruba o boot. Ver apps/tv/src/packaged-tv-scale.ts.
applyPackagedTvScale()

// Gate da BASE de TV (legibilidade lean-back a 3m): mecanismo INDEPENDENTE do de
// cima. Em TODA TV real (por UA, qualquer protocolo), marca html[data-tv-scale] →
// o index.css troca a base inteira (tipografia, geometria de card, safe area)
// pelos valores medidos pra o canvas 1920. NÃO é um multiplicador: o --ui-scale
// fica NEUTRO (1) na TV — multiplicar a base já rebaseada dobrava a escala e
// derrubava a grade de 6 pra 5 colunas. NO-OP no web/mobile (UA não é TV) → base
// do tokens.css intacta. Idempotente. Ver apps/tv/src/packaged-tv-scale.ts.
applyTvUiScale()

// SW auto-recovery logo após imports, antes do React boot. Detecta SW
// corrompido (waitUntil/CacheFirst.handle errors) e auto-recupera
// (unregister + clear caches + reload). Sem isso, user fica preso em SW
// velho buggy até descobrir como limpar cache manualmente.
// Ver apps/tv/src/sw-recovery.ts (camada 1 da defesa em 4 camadas).
initSwAutoRecovery()

// Sentry NÃO inicia mais AQUI (era o 2º motivo do eval do SDK cair na janela
// pré-paint — o 1º era o catch-all do manualChunks jogando @sentry no vendor
// eager, já corrigido). `initSentryIfConfigured()` foi MOVIDO pro fim de
// `start()`, dentro do `deferAfterFirstPaint` (double-rAF), DEPOIS da marca de
// paint. O `await import('@sentry/...')` mora lá dentro, então o chunk `sentry`
// (agora fora do vendor) só é buscado/avaliado após a tela pintar. Erros ANTES
// do init (raros) já são capturados pelo `window.onerror` gravado no index.html
// (window.__bootErrors) — nenhuma captura crítica se perde.

// FONTES DE RUNTIME CONFIG — uma CORRENTE, não uma URL única (§4 do plano
// docs/plan/origem-da-api-sem-rebuild.md). Cada degrau é ADITIVO: falhar num não
// impede o seguinte; o piso (sementes embutidas) é literal e não pode falhar.
//
// As origens de API embutidas (`API_SEED_ORIGINS`: env de build → primária,
// sementes de resiliência atrás, sem duplicata) viram fontes de bootstrap
// `${origem}/v1/config/runtime`. A base NUNCA é lida do env AQUI: era a leitura
// duplicada neste ponto que, com env ausente, montava '/v1/config/runtime' —
// root-relativo, que em `file://` vira `file:///v1/config/runtime` e deixava o app
// empacotado sem forma de achar a API (incidente do .ipk 2.24.2). A lista vem de
// runtime-config.ts, fonte única.
const seedBootstrapUrls = API_SEED_ORIGINS.map((origin) => `${origin}/v1/config/runtime`)

// VITE_RUNTIME_CONFIG_URL (domínio de config estático em apex separado, futuro P4)
// entra como PRIMEIRO degrau — mas NÃO substitui a API: as fontes-semente seguem
// na lista ATRÁS dele. É o invariante que evita a armadilha "ou/ou": se o domínio
// de config cair, a API continua sendo fonte de config. Camada nova só ADICIONA
// degrau acima, nunca remove um de baixo.
const rawConfigDomain = import.meta.env['VITE_RUNTIME_CONFIG_URL'] as string | undefined
const configDomainUrl = rawConfigDomain ? rawConfigDomain.trim() : ''
const bootstrapUrls = configDomainUrl
  ? [configDomainUrl, ...seedBootstrapUrls]
  : seedBootstrapUrls

declare global {
  interface Window {
    __bootFailsafe?: ReturnType<typeof setTimeout> | undefined
  }
}

function hideSplash(): void {
  const splash = document.getElementById('boot-splash')
  if (splash) splash.style.display = 'none'
  const failsafe = window.__bootFailsafe
  if (failsafe) {
    clearTimeout(failsafe)
    delete window.__bootFailsafe
  }
}

// P3 (perf/tv-hero-coldstart): revalidação do runtime config FORA do caminho
// crítico. loadRuntimeConfig é resiliente (corrente de fontes com timeout curto
// por degrau + cache localStorage + sementes embutidas) e sempre resolve — aqui
// ele roda em background e o resultado sobrescreve `active` (reativo).
// killSwitch/banner/minAppVersion ficam
// disponíveis em `getRuntimeConfig()` assim que o fetch volta; um consumidor
// reativo (quando existir) lê o valor fresco daqui, aplicado DEPOIS do paint.
async function revalidateRuntimeConfig(): Promise<void> {
  try {
    const cfg = await loadRuntimeConfig(bootstrapUrls, API_SEED_ORIGINS)
    console.warn(
      `[boot] runtime config revalidated: ${cfg.api.endpoints.length} endpoint(s), killSwitch=${cfg.killSwitch}`,
    )
  } catch (err) {
    // loadRuntimeConfig nunca rejeita (resolve pro fallback); catch é só cinto de
    // segurança pra não virar unhandledrejection no fleet 2017.
    console.warn('[boot] runtime config revalidate skip', err)
  }
}

async function start() {
  try {
    console.warn('[boot] starting...')

    // P3 (perf/tv-hero-coldstart): runtime config NÃO bloqueia mais o 1º paint.
    // `active` já está semeado SÍNCRONO com o fallback de build (makeFallback no
    // load de runtime-config.ts) — getActiveApiUrl/getActiveSiteUrl já respondem,
    // então wireRuntimeConfig e bootstrap seguem na hora. Antes, o
    // `await loadRuntimeConfig` segurava o boot por ATÉ 3s (fetch no-store roda
    // ANTES do fallback), mesmo com o fallback pronto.
    //
    // Plugamos as funções do runtime-config no api.ts (evita import cycle).
    // O transporte de telemetria herda daqui via getApiBase() — usa a base
    // ATIVA, mas nunca rotaciona endpoint (ver telemetry-transport.ts).
    wireRuntimeConfig({ getActiveApiUrl, getActiveSiteUrl, rotateApiUrl, resetApiUrl })
    // Revalidação REATIVA em background: sobrescreve `active` com o config remoto
    // (endpoints de failover, killSwitch, banner, minAppVersion) quando/se volta,
    // sem bloquear o paint. Não-awaited de propósito.
    void revalidateRuntimeConfig()

    // Inicializa tier + spatial nav + i18n (lazy do idioma) antes do primeiro render
    await bootstrap()
    console.warn('[boot] bootstrap done')

    // MSW (dev only). Se falhar, segue sem mock — não bloqueia render.
    await setupMockServer().catch((err) => console.warn('[boot] mock skip', err))
    console.warn('[boot] mock done')
  } catch (err) {
    console.error('[boot] init failed', err)
    // Renderiza mesmo assim — telas mostram erro de fallback
  }

  const root = document.getElementById('root')
  if (!root) {
    console.error('[boot] #root not found')
    hideSplash()
    return
  }

  // Marca 3/4: instante em que o React vai montar (fim do bootstrap).
  markBoot('boot:create-root')
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )

  // Splash some assim que React monta
  hideSplash()
  console.warn('[boot] mounted')

  // Marca 4/4 (1º paint) + init DEFERIDO do Sentry. `deferAfterFirstPaint` é o
  // MESMO double-rAF de antes (callback do 1º rAF roda ANTES do paint; o 2º
  // garante que o 1º frame pintou; Chr38 tem rAF/Chr10+), extraído pra
  // boot-defer.ts pra virar testável. Dentro do callback, NESTA ORDEM: 1º a marca
  // de paint, DEPOIS o init do Sentry — que faz o `await import('@sentry/...')`
  // lá dentro, então o chunk `sentry` só é buscado/avaliado FORA da janela
  // pré-paint. Se rAF faltar/lançar, o callback roda síncrono (a marca vira
  // best-effort, mas o Sentry NÃO se perde — ver boot-defer.ts). ⚠️ NÃO trocar
  // por requestIdleCallback: é Chr47+ e NÃO existe no piso Chr38 (ReferenceError
  // = tela branca).
  deferAfterFirstPaint(() => {
    markBoot('boot:first-paint')
    void initSentryIfConfigured()
  })

  void registerServiceWorker()

  const tier = document.documentElement.dataset['tier'] ?? 'mid'
  startQoe(tier)
  // health-watcher (FPS + heap + IDB + long-session + cleanup) inicia
  // em providers.tsx — precisa do QueryClient pra GC de queries inativas.
  // Consolidou o antigo startFpsWatchdog daqui (FPS sample agora no
  // mesmo módulo do long-session-cleaner) com telemetria correlacionada.
}

void start()
